import React, { useEffect, useState } from "react";
import { Save, ExternalLink, MapPin, ChevronRight, ArrowLeft, CheckCircle2, Trash2, ChevronDown, Video, Quote as QuoteIcon } from "lucide-react";
import { adminApi } from "../../lib/api";
import { toast } from "sonner";
import { LOCATIONS, PRIMARY_LOCATION_SLUGS } from "../../data/locations";
import VideoUpload from "./VideoUpload";

const emptyT = { quote: "", author: "", role: "", location: "", video_url: "" };

const primaryTowns = PRIMARY_LOCATION_SLUGS.map((s) => LOCATIONS.find((l) => l.slug === s)).filter(Boolean);
const primarySet = new Set(PRIMARY_LOCATION_SLUGS);
const additionalTowns = LOCATIONS.filter((l) => !primarySet.has(l.slug));

export default function TownTestimonialsManager() {
  const [data, setData] = useState({});
  const [active, setActive] = useState(null); // active town slug or null
  const [existing, setExisting] = useState([]);
  const [form, setForm] = useState(emptyT);
  const [saving, setSaving] = useState(false);
  const [showAdditional, setShowAdditional] = useState(false);

  useEffect(() => { adminApi.townTestimonials.get().then((d) => setData(d || {})).catch(() => {}); }, []);
  useEffect(() => { adminApi.writtenTestimonials.list().then((d) => setExisting(d || [])).catch(() => {}); }, []);

  const openTown = (slug) => {
    const t = data[slug] || emptyT;
    setForm({ quote: t.quote || "", author: t.author || "", role: t.role || "", location: t.location || "", video_url: t.video_url || "" });
    setActive(slug);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const persist = async (next) => {
    setSaving(true);
    try {
      const saved = await adminApi.townTestimonials.update(next);
      setData(saved || next);
      toast.success("Town page testimonial saved");
      return true;
    } catch {
      toast.error("Could not save");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    const next = { ...data, [active]: { ...form } };
    if (await persist(next)) setActive(null);
  };

  const clearTown = async () => {
    const next = { ...data, [active]: { ...emptyT } };
    setForm(emptyT);
    if (await persist(next)) setActive(null);
  };

  const activeLoc = active ? LOCATIONS.find((l) => l.slug === active) : null;
  const hasContent = (slug) => !!((data[slug]?.quote || "").trim() || (data[slug]?.video_url || "").trim());
  const hasVideo = (slug) => !!(data[slug]?.video_url || "").trim();

  const TownTile = (l) => (
    <button
      key={l.slug}
      data-testid={`town-tile-${l.slug}`}
      onClick={() => openTown(l.slug)}
      className="group text-left bg-white rounded-2xl border border-slate-200 p-5 shadow-sm hover:shadow-lg hover:border-orange/40 hover:-translate-y-0.5 transition-all"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="w-11 h-11 rounded-xl bg-navy/5 text-navy flex items-center justify-center group-hover:bg-orange group-hover:text-white transition-colors"><MapPin size={20} /></div>
        <ChevronRight size={18} className="text-slate-300 group-hover:text-orange transition-all" />
      </div>
      <p className="font-display font-bold text-navy leading-tight">{l.town}</p>
      <p className="text-xs text-slate-400 mt-0.5">{l.county}</p>
      <span className={`inline-flex items-center gap-1.5 mt-3 text-xs font-bold ${hasContent(l.slug) ? "text-green-600" : "text-slate-400"}`}>
        {hasContent(l.slug)
          ? <>{hasVideo(l.slug) ? <Video size={13} /> : <CheckCircle2 size={13} />} {hasVideo(l.slug) ? "Video added" : "Testimonial added"}</>
          : "No testimonial yet"}
      </span>
    </button>
  );

  // ---- Town list ----
  if (!active) {
    return (
      <div>
        <h1 className="font-display font-extrabold text-2xl text-navy mb-1">Town Pages</h1>
        <p className="text-slate-500 text-sm mb-8 max-w-2xl">
          Add a written or video testimonial for each town's local page. Tip: use a genuine Google review (written) or a client video from a business based in or near that town. A town page only shows a testimonial once you've added one — empty towns show nothing.
        </p>

        <h2 className="text-xs font-bold uppercase tracking-widest text-orange mb-3">Priority towns</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="priority-towns-grid">
          {primaryTowns.map((l) => TownTile(l))}
        </div>

        <button
          data-testid="additional-towns-toggle"
          onClick={() => setShowAdditional((v) => !v)}
          className="mt-8 inline-flex items-center gap-2 text-sm font-bold text-navy bg-white border border-slate-200 rounded-full px-5 py-2.5 hover:border-orange/40 hover:text-orange transition-colors"
        >
          <ChevronDown size={16} className={`transition-transform ${showAdditional ? "rotate-180" : ""}`} />
          Additional Towns ({additionalTowns.length})
        </button>

        {showAdditional && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-5" data-testid="additional-towns-grid">
            {additionalTowns.map((l) => TownTile(l))}
          </div>
        )}
      </div>
    );
  }

  // ---- Single-town editor ----
  return (
    <div>
      <div className="flex items-center justify-between mb-1 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button data-testid="town-back" onClick={() => setActive(null)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-sm font-semibold text-navy hover:border-orange/40 hover:text-orange transition-colors"><ArrowLeft size={15} /> All towns</button>
          <h1 className="font-display font-extrabold text-2xl text-navy">{activeLoc?.town} testimonial</h1>
        </div>
        <a href={`/${active}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-semibold text-orange"><ExternalLink size={14} /> View {activeLoc?.town} page</a>
      </div>
      <p className="text-slate-500 text-sm mb-8 max-w-2xl">Add a written quote, a video testimonial, or both for {activeLoc?.town}. Leave both empty to hide the testimonial on this town's page.</p>

      <div className="max-w-2xl space-y-6">
        {/* Written testimonial */}
        <div className="space-y-5 bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
          <div className="flex items-center gap-2 text-navy font-display font-bold"><QuoteIcon size={16} className="text-orange" /> Written testimonial</div>
          {existing.length > 0 && (
            <div className="rounded-xl bg-slate-50 border border-slate-100 p-4" data-testid="town-reuse-block">
              <label className="block text-sm font-semibold text-navy mb-1.5">Reuse one of your existing testimonials</label>
              <select
                data-testid="town-reuse-select"
                value=""
                onChange={(e) => {
                  const t = existing[Number(e.target.value)];
                  if (!t) return;
                  setForm((f) => ({ ...f, quote: t.quote || "", author: t.name || "", role: t.role || "" }));
                }}
                className={inputCls}
              >
                <option value="">Choose a testimonial to copy in…</option>
                {existing.map((t, i) => (
                  <option key={t.id || i} value={i}>{t.name}{t.role ? ` — ${t.role}` : ""}</option>
                ))}
              </select>
              <p className="text-xs text-slate-500 mt-2">
                Copies the quote, name and role in so you don't have to retype them. Only pick a client who is genuinely based in or near {activeLoc?.town} — then add the "Based in" line below yourself.
              </p>
            </div>
          )}
          <div>
            <label className="block text-sm font-semibold text-navy mb-1.5">Testimonial / review quote</label>
            <textarea data-testid="town-quote" rows={5} value={form.quote} onChange={(e) => set("quote", e.target.value)} className={inputCls} placeholder="e.g. Paul and the team have been fantastic — we finally understand our numbers…" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-navy mb-1.5">Author name</label>
              <input data-testid="town-author" value={form.author} onChange={(e) => set("author", e.target.value)} className={inputCls} placeholder="e.g. Louise McCarthy" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-navy mb-1.5">Author role / business <span className="font-normal text-slate-400">(optional)</span></label>
              <input data-testid="town-role" value={form.role} onChange={(e) => set("role", e.target.value)} className={inputCls} placeholder={`e.g. Director, ${activeLoc?.town} business`} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-navy mb-1.5">Based in <span className="font-normal text-slate-400">(optional — shows a "Based in …" label for local relevance)</span></label>
            <input data-testid="town-location" value={form.location} onChange={(e) => set("location", e.target.value)} className={inputCls} placeholder={activeLoc?.town} />
          </div>
        </div>

        {/* Video testimonial */}
        <div className="space-y-4 bg-white rounded-2xl border border-slate-100 p-6 shadow-sm" data-testid="town-video-section">
          <div className="flex items-center gap-2 text-navy font-display font-bold"><Video size={16} className="text-orange" /> Video testimonial <span className="font-normal text-slate-400 text-sm">(optional)</span></div>
          <VideoUpload label="" hint="Upload a client video testimonial (max 300MB). It appears on the town page with the written quote as a caption." value={form.video_url} onChange={(v) => set("video_url", v)} />
        </div>

        <div className="flex items-center gap-3">
          <button data-testid="town-save-btn" onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark disabled:opacity-60 text-white font-bold px-6 py-2.5 rounded-full transition-colors">
            <Save size={16} /> {saving ? "Saving..." : "Save testimonial"}
          </button>
          {hasContent(active) && (
            <button data-testid="town-clear-btn" onClick={clearTown} disabled={saving} className="inline-flex items-center gap-2 bg-white border border-slate-200 hover:border-red-300 hover:text-red-500 text-slate-500 font-bold px-5 py-2.5 rounded-full transition-colors">
              <Trash2 size={15} /> Remove
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange/40";
